import { useEffect, useRef } from 'react';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Label,
    makeStyles,
    Radio,
    RadioGroup,
    Spinner,
    Textarea,
    tokens,
} from '@fluentui/react-components';

import { useTranslation } from 'react-i18next';
import { RootState } from '../../redux/app/store';
import {
    addAlert,
    setFeedbackIsOpen,
    setFeedbackIsSending,
    setFeedbackValue,
    setFeedbackContext,
    setFeedbackRating,
} from '../../redux/features/app/appSlice';
import { AlertType } from '../../libs/models/AlertType';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { useSelector } from 'react-redux';
import { IChatMessage } from '../../libs/models/ChatMessage';
import { PostEvent } from '../../embed-script/iframeWindow';
import { isMessageFromHost } from '../../embed-script/messageOrigin';
import { rememberParentOrigin } from '../../libs/parentOrigin';
import { FlexDialogActions, PrimaryButton, SecondaryButton, SpinnerContainer } from './StyledCommonComponents';
import { useFeedback } from '../../libs/hooks/useFeedback';
import { formatChatMessage } from '../utils/ChatMessageUtils';

const env = process.env.NODE_ENV;
const appName = process.env.REACT_APP_APPLICATIONNAME;
const deployEnv = process.env.REACT_APP_ENVIRONMENT;

const useClasses = makeStyles({
    dialogSurface: {
        borderRadius: '16px',
    },
    dialogContentText: {
        '& a': {
            color: tokens.colorBrandForegroundLink,
        },
        '& a:hover': {
            color: tokens.colorBrandForegroundLinkHover,
        },
    },
});

export const FeedbackDialogOverlay = () => {
    const classes = useClasses();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { isOpen, isSending, value, isPositive, context, rating, hideRating } = useSelector(
        (state: RootState) => state.app.feedbackForm,
    );
    const { email } = useSelector((state: RootState) => state.app.activeUserInfo) ?? {};
    const hostContext = useSelector((state: RootState) => state.hostContext.hostContext);

    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { postFeedback } = useFeedback();

    useEffect(() => {
        function handlePostMessage(event: MessageEvent<PostEvent>) {
            // CWE-346: ignore messages that don't originate from our embedding parent frame, and
            // treat the payload as untrusted — postMessage data is arbitrary regardless of its type.
            // Inline dup of the source check so Coverity's UNCHECKED_ORIGIN sees it — rationale in messageOrigin.ts.
            const payload: unknown = event.data;
            if (event.source !== window.parent || !isMessageFromHost(event) || !payload || typeof payload !== 'object')
                return;
            // CWE-668 (#2113): a message that passed the source check carries our real embedder's
            // origin — remember it so iframe → host posts target it instead of '*' (see parentOrigin.ts).
            rememberParentOrigin(event.origin);
            const ev: PostEvent = event.data;
            if (ev.event === 'hashchange') {
                const data = ev.data as { hash: string; title: string | undefined };
                dispatch(setFeedbackContext({ url: data.hash, pageTitle: data.title }));
            }
        }

        window.addEventListener('message', handlePostMessage);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('message', handlePostMessage);
        };
    }, [dispatch]);

    useEffect(() => {
        if (isOpen && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isOpen]);

    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);

    const handleSendFeedback = () => {
        if (isSending) return;

        dispatch(setFeedbackIsSending(true));

        const formatMessage = (message: IChatMessage) => {
            // #1662: the store now keeps raw [[#n]] citation markers — formatting moved to the
            // render layer so streamed answers show source chips. Route through formatChatMessage
            // so the feedback report carries the numbered <sup> the store used to bake in (and the
            // trailing follow-up-questions JSON gets stripped), not literal [[#1]] tokens.
            const content = formatChatMessage(message);
            if (message.userId.toLowerCase() === 'bot') {
                return `bot> ${content}`;
            } else {
                return `user> ${content}`;
            }
        };

        // AIA-73: there is a eslint ts error because of wrongly defined Record on messages, as it can be null
        // fixing that error gives another 94 errors all over the codebase.
        /* eslint-disable @typescript-eslint/no-unnecessary-condition */
        const selectedMessages = conversations[selectedId]?.messages ?? [];
        const chatLog = selectedMessages.map(formatMessage).join('\n');
        const messageIndex = selectedMessages.findIndex((msg) => msg.id === context.messageId);
        const feedbackedMsg = (
            selectedMessages && messageIndex !== undefined && messageIndex > 0
                ? selectedMessages.slice(messageIndex - 1, messageIndex + 1)
                : []
        )
            .map(formatMessage)
            .join('\n');
        /* eslint-enable @typescript-eslint/no-unnecessary-condition */
        const sourceLinks = context.contentMetadata
            ?.filter((element) => element.orderOfAppearance > 0)
            .map((tuple) => tuple.url ?? '');

        postFeedback({
            text: value,
            history: chatLog,
            env,
            appName,
            isPositive,
            sourceLinks,
            chatId: context.chatId,
            messageId: context.messageId,
            feedbackedMsg,
            email,
            url: context.url,
            pageTitle: context.pageTitle,
            // The star scale is hidden on the thumbs path, so a `rating` reaching here on that path
            // is never something the user picked in *this* dialog. Closing does reset the form, so
            // this is a guard rather than a fix for a reachable path today — but the cost of it
            // being wrong is high: #2397 keys its de-duplication on "does this submission add
            // anything the thumbs click didn't", so a phantom rating posts the same feedback to
            // Slack twice.
            rating: hideRating ? null : rating,
            hostId: hostContext.id,
            products: hostContext.products,
            country: hostContext.country,
            deployEnv,
        })
            .then((_) => {
                dispatch(setFeedbackIsOpen({ isOpen: false, hideRating: false }));
                dispatch(setFeedbackContext({}));
                dispatch(setFeedbackRating(null));
            })
            .catch((e: unknown) => {
                console.error(e);
                dispatch(addAlert({ message: t('feedback.errorSending'), type: AlertType.Error }));
            })
            .finally(() => {
                dispatch(setFeedbackIsSending(false));
            });
    };

    return (
        <Dialog
            modalType="modal"
            open={isOpen}
            onOpenChange={(_, data) => {
                dispatch(setFeedbackIsOpen({ isOpen: data.open, hideRating: false }));
            }}
        >
            <DialogSurface className={classes.dialogSurface}>
                <DialogBody>
                    <DialogTitle>{t('feedback.dialog.title')}</DialogTitle>
                    <DialogContent style={{ whiteSpace: 'pre-wrap' }}>
                        <div
                            className={classes.dialogContentText}
                            dangerouslySetInnerHTML={{ __html: t('feedback.dialog.content') }}
                        />
                        <Textarea
                            ref={textareaRef}
                            onChange={(_, data) => {
                                dispatch(setFeedbackValue(data.value));
                            }}
                            placeholder={t('feedback.dialog.placeholder')}
                            style={{
                                width: '100%',
                                marginTop: '1em',
                                borderRadius: tokens.borderRadiusMedium,
                                border: `1px solid ${tokens.colorNeutralForeground3}`,
                                outline: 'none',
                            }}
                            required
                            id={'feedback-text'}
                        />
                        {!hideRating && (
                            <>
                                <Label>{t('feedback.dialog.ratingLabel')}</Label>
                                <RadioGroup
                                    layout="horizontal"
                                    onChange={(_, data) => {
                                        dispatch(setFeedbackRating(data.value));
                                    }}
                                >
                                    {[1, 2, 3, 4, 5].map((value) => (
                                        <Radio key={value} value={value.toString()} label={value.toString()} />
                                    ))}
                                </RadioGroup>
                            </>
                        )}
                    </DialogContent>
                    <FlexDialogActions style={{ display: 'flex', flexWrap: 'nowrap' }}>
                        <DialogTrigger disableButtonEnhancement>
                            <SecondaryButton>{t('feedback.dialog.cancelButton')}</SecondaryButton>
                        </DialogTrigger>
                        {isSending ? (
                            <SecondaryButton>
                                <SpinnerContainer>
                                    <Spinner />
                                </SpinnerContainer>
                            </SecondaryButton>
                        ) : (
                            <PrimaryButton onClick={handleSendFeedback}>
                                {t('feedback.dialog.submitButton')}
                            </PrimaryButton>
                        )}
                    </FlexDialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
